import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import {
  LoadingState,
  ErrorState,
  StrengthBar,
  AttentionBadge,
  AskAboutThis,
  directionInfo,
  type Tone,
} from "@/components/research-hub";
import { useManagerConsistency, type ManagerStanceRow } from "@/hooks/useResearchHubAnalyst";

// "Where managers disagree" — multi-cohort / manager-consistency divergence.
// The backing data (Street-consensus / T3 sell-side feeds) is not connected yet, so the hook
// reports available:false and we show a calm awaiting-data state. The full layout below is
// ready and renders automatically the moment the feed + RPC are in place — no further build.

type Subject = {
  subject: string;
  ourDirection: string | number | null | undefined;
  managers: ManagerStanceRow[];
  nAgree: number;
  nTotal: number;
};

function groupBySubject(rows: ManagerStanceRow[]): Subject[] {
  const map = new Map<string, ManagerStanceRow[]>();
  for (const r of rows) {
    const key = r.subject_label ?? "A view";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  return Array.from(map.entries()).map(([subject, group]) => {
    const first = group[0] ?? {};
    const ourDirection = first.our_direction;
    const ourWord = directionInfo(ourDirection).word;
    const managers = group.filter((g) => g.manager_label || g.manager_direction);

    // Subject-level counts are scalar for the subject. A one-row-per-manager payload repeats the same
    // cohort totals on every row, so SUMMING them would multiply the totals by the manager count —
    // take the counts from a single row that carries them instead. Derive the total from
    // agree + disagree when n_managers is absent.
    const rowWithCounts = group.find((g) => g.n_managers != null || g.n_agree != null || g.n_disagree != null);
    let explicitTotal = rowWithCounts?.n_managers ?? null;
    const explicitAgree = rowWithCounts?.n_agree ?? null;
    const explicitDisagree = rowWithCounts?.n_disagree ?? null;
    if (explicitTotal == null && (explicitAgree != null || explicitDisagree != null)) {
      explicitTotal = (explicitAgree ?? 0) + (explicitDisagree ?? 0);
    }

    // Otherwise derive from each manager's direction vs ours (only counts as agreement when both our
    // direction and the manager's direction are known and match).
    const derivedTotal = managers.length;
    const derivedAgree = ourWord
      ? managers.filter((m) => directionInfo(m.manager_direction).word === ourWord).length
      : 0;

    const hasExplicit = explicitTotal != null && explicitTotal > 0;
    const nTotal = hasExplicit ? (explicitTotal as number) : derivedTotal;
    const rawAgree = hasExplicit ? explicitAgree ?? 0 : derivedAgree;
    const nAgree = Math.max(0, Math.min(rawAgree, nTotal)); // never report more agreers than the cohort
    return { subject, ourDirection, managers, nAgree, nTotal };
  });
}

export default function CohortDivergence() {
  const { data, isLoading, error, refetch } = useManagerConsistency();
  const available = data?.available ?? false;
  const rows = data?.rows ?? [];

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="space-y-2">
        <h1 className="font-heading text-3xl font-bold text-primary">Where managers disagree</h1>
        <p className="font-body text-sm text-muted-foreground max-w-2xl">
          How our views line up against the wider market of managers, view by view.
        </p>
      </div>

      {isLoading ? (
        <LoadingState rows={3} />
      ) : error ? (
        <ErrorState onRetry={() => refetch()} />
      ) : !available || rows.length === 0 ? (
        <Card className="p-8 flex flex-col items-center text-center gap-3 font-body">
          <Users className="h-7 w-7 text-muted-foreground" aria-hidden />
          <h2 className="font-heading text-base font-medium">Waiting on consensus feeds</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            This compares our views against other managers' positioning. It lights up once the
            market-consensus feeds are connected. Until then, you can see where our own views are
            under pressure — against the market and against each other.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-1">
            <Link to="/partner/research-hub/workbench/divergence">Where we diverge</Link>
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {groupBySubject(rows).map((g) => (
            <SubjectCard key={g.subject} group={g} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubjectCard({ group }: { group: Subject }) {
  const our = directionInfo(group.ourDirection);
  const agree = group.nTotal > 0 ? Math.min(1, Math.max(0, group.nAgree / group.nTotal)) : 0;
  // Calm verdict: are managers mostly with us, split, or mostly against?
  let label = "Managers are split";
  let tone: Tone = "neutral";
  if (group.nTotal > 0) {
    if (agree >= 0.66) {
      label = "Managers mostly agree with us";
      tone = "good";
    } else if (agree <= 0.34) {
      label = "Managers mostly disagree";
      tone = "bad";
    }
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-base font-medium truncate">
            {our.word && <span className="mr-1">{our.word[0].toUpperCase() + our.word.slice(1)}:</span>}
            {group.subject}
          </div>
          {group.nTotal > 0 && (
            <div className="font-body text-xs text-muted-foreground mt-0.5">
              {group.nAgree} of {group.nTotal} managers take our side
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <AttentionBadge label={label} tone={tone} />
          <AskAboutThis prefill={`How do other managers see ${group.subject} compared with us?`} label="" />
        </div>
      </div>

      {group.nTotal > 0 && <StrengthBar value={agree} label="agreement" />}

      {group.managers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {group.managers.map((m, i) => {
            const d = directionInfo(m.manager_direction);
            return (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 font-body text-[11px] text-muted-foreground"
              >
                {m.manager_label ?? "A manager"} {d.glyph}
              </span>
            );
          })}
        </div>
      )}
    </Card>
  );
}
